import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { landingAnalytics, getTimeOnPage } from '../services/landingAnalytics';

const EmailCaptureModal = ({ open, onClose, source = 'unknown', headlineVariant, ctaVariant, demoAnalytics = null, colorTheme = 'teal' }) => {
  const theme = useTheme();
  const { trackEvent } = useAnalytics();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [course, setCourse] = useState('');
  const [otherCourse, setOtherCourse] = useState('');
  const [challenge, setChallenge] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Course options
  const courseOptions = [
    { value: 'year11_ext1', label: 'Year 11 Extension 1' },
    { value: 'year11_advanced', label: 'Year 11 Advanced' },
    { value: 'year12_ext1', label: 'Year 12 Extension 1' },
    { value: 'year12_advanced', label: 'Year 12 Advanced' },
    { value: 'other', label: 'Other (please specify)' },
  ];

  // Track when modal opens
  useEffect(() => {
    if (open) {
      landingAnalytics.trackFunnelStep('email_modal_opened', {
        source,
        headline_variant: headlineVariant,
        cta_variant: ctaVariant,
        time_on_page: getTimeOnPage()
      });
      
      // Track time to interaction
      landingAnalytics.trackTimeToInteraction('email_modal_open', getTimeOnPage());
    }
  }, [open, source, headlineVariant, ctaVariant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate name
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Validate course selection
    if (!course) {
      setError('Please select your course');
      return;
    }
    
    // If 'other' is selected, validate the other course field
    if (course === 'other' && !otherCourse.trim()) {
      setError('Please specify your course');
      return;
    }
    
    // Validate challenge response is required
    if (!challenge.trim()) {
      setError('Please tell us your biggest challenge when studying for maths');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Track submission attempt
      trackEvent('email_modal_submit_attempt', {
        source,
        has_challenge: !!challenge,
        headline_variant: headlineVariant,
        cta_variant: ctaVariant,
      });

      // Prepare signup data
      const signupData = {
        name,
        email,
        course: course === 'other' ? otherCourse : course,
        course_type: course, // Store the original selection for analytics
        challenge_response: challenge,
        source,
        ab_test_data: {
          headline_variant: headlineVariant,
          cta_variant: ctaVariant,
        },
        user_type: 'student',
      };

      // Include demo analytics if available
      if (demoAnalytics) {
        signupData.demo_analytics = demoAnalytics.getSignupAnalytics();
      }

      // API call to submit email
      const response = await fetch('/api/waitlist/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(signupData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to join waitlist');
      }

      // Track success
      trackEvent('email_submitted', {
        source,
        has_challenge: !!challenge,
        headline_variant: headlineVariant,
        cta_variant: ctaVariant,
      });

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      trackEvent('email_submit_error', {
        source,
        error: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      trackEvent('email_modal_closed', {
        source,
        was_success: success,
      });
      onClose();
      // Reset form on close
      setTimeout(() => {
        setName('');
        setEmail('');
        setCourse('');
        setOtherCourse('');
        setChallenge('');
        setError(null);
        setSuccess(false);
      }, 300);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          position: 'relative',
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        }}
        disabled={loading}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ pr: 6 }}>
        {success ? (
          <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 600 }}>
            You're on the list! 🎉
          </Typography>
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Get Early Access
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Thanks for joining! We'll send you an email with early access as soon as we're ready.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In the meantime, why not try our demo? It's a great way to see what we're building.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Join thousands of HSC students getting ahead. Be the first to know when we launch with exclusive early access.
            </Typography>

            <TextField
              fullWidth
              label="Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              required
              sx={{ mb: 2 }}
              autoFocus
            />

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>Course</InputLabel>
              <Select
                value={course}
                onChange={(e) => setCourse(e.target.value)}
                label="Course"
                disabled={loading}
              >
                {courseOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {course === 'other' && (
              <TextField
                fullWidth
                label="Please specify your course"
                type="text"
                value={otherCourse}
                onChange={(e) => setOtherCourse(e.target.value)}
                disabled={loading}
                required
                sx={{ mb: 2 }}
                placeholder="e.g., Year 10, Year 12 Extension 2, University, etc."
              />
            )}

            <TextField
              fullWidth
              label="What's your biggest challenge when studying for maths?"
              multiline
              rows={2}
              value={challenge}
              onChange={(e) => setChallenge(e.target.value)}
              disabled={loading}
              required
              sx={{ mb: 2 }}
              helperText="Help us build the perfect study tool for you"
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !email}
              startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              sx={{
                py: 1.5,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              {loading ? 'Joining...' : 'Join the Waitlist'}
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 2,
              }}
            >
              No spam, ever. We'll only email you about early access.
              <br />
              By signing up, you agree to our <Link href="/privacy" target="_blank" sx={{ color: 'inherit' }}>Privacy Policy</Link>.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {success && (
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default EmailCaptureModal;